import { useApolloClient } from '@apollo/react-hooks'
import { mapUserToUserPersona, User } from 'model'
import { IPersonaProps } from 'office-ui-fabric-react'
import { isValidMailAddress } from '../helpers'
import {
  UserByIdDocument,
  UserByIdQuery,
  UserSuggestionsDocument,
  UserSuggestionsQuery,
} from './useUserSuggestions.generated'

export type UserToUserPersonaMapper = (
  user: User,
  defaultMapper?: typeof mapUserToUserPersona
) => IPersonaProps

export const useUserSuggestions = (
  mapUserToUserPersonaCustom?: UserToUserPersonaMapper,
  allowMailAddress = false,
  customSuggestions?: User[]
) => {
  const client = useApolloClient()

  const findUser = async (id: string) => {
    if (!id) return null
    try {
      const result = await client.query<UserByIdQuery>({
        query: UserByIdDocument,
        fetchResults: true,
        variables: { id },
      })
      return result.data && result.data.user ? result.data.user : null
    } catch (e) {
      // tslint:disable-next-line:no-console
      console.log(`User ${id} not found`)
      return null
    }
  }

  const suggestUsers = async (
    filter: string,
    selectedItems?: IPersonaProps[]
  ) => {
    if (filter.length > 2 || customSuggestions) {
      let users: User[]
      if (customSuggestions) {
        users = customSuggestions
      } else {
        const result = await client.query<UserSuggestionsQuery>({
          query: UserSuggestionsDocument,
          fetchResults: true,
          variables: { filter },
        })
        users =
          result.data && result.data.users ? (result.data.users as User[]) : []
      }

      if (allowMailAddress) {
        users = users.filter((x) => x.email)
        if (users.length === 0 && isValidMailAddress(filter)) {
          return [
            {
              userId: filter,
              text: filter,
            } as IPersonaProps,
          ]
        }
      }

      if (selectedItems) {
        users = users.filter(
          (user) =>
            !selectedItems
              .map((persona: any) => persona.userId)
              .includes(user._id)
        )
      }

      return users.map((user) => {
        return mapUserToUserPersonaCustom
          ? mapUserToUserPersonaCustom(user, mapUserToUserPersona)
          : mapUserToUserPersona(user)
      })
    } else {
      return []
    }
  }

  return {
    suggestUsers,
    suggestAllUsers: (selectedItems?: IPersonaProps[]) =>
      suggestUsers('', selectedItems),
    findUser,
  }
}
